export const ACCOUNT_KINDS = Object.freeze({
  expense: "Gasto", asset: "Activo", liability: "Pasivo", equity: "Patrimonio", income: "Ingreso",
});

const identifier = (value) => value === null || value === undefined || value === "" ? null : String(value);
const accountTitle = (account) => [account.code, account.name].filter(Boolean).join(" · ") || `Cuenta #${account.id}`;
const sortAccounts = (left, right) => String(left.code || "").localeCompare(String(right.code || ""), "es", { numeric: true })
  || String(left.name || "").localeCompare(String(right.name || ""), "es")
  || String(left.id).localeCompare(String(right.id), "es", { numeric: true });

// Flatten the supplied chart once, before filters. Depth and full paths remain
// useful when a type filter or historical reference omits a parent from view.
export function accountRows(accounts = [], { kind, postableOnly = false } = {}) {
  const unique = new Map();
  for (const account of Array.isArray(accounts) ? accounts : []) {
    const id = identifier(account?.id);
    if (id && !unique.has(id)) unique.set(id, account);
  }
  const children = new Map();
  for (const account of unique.values()) {
    const parent = identifier(account.parent_account_id);
    if (parent && unique.has(parent)) {
      if (!children.has(parent)) children.set(parent, []);
      children.get(parent).push(account);
    }
  }
  for (const branch of children.values()) branch.sort(sortAccounts);
  const roots = [...unique.values()].filter((account) => !unique.has(identifier(account.parent_account_id))).sort(sortAccounts);
  const visited = new Set();
  const flattened = [];
  function branch(root, issue) {
    const stack = [{ account: root, depth: 0, path: [], issue, ancestorInactive: false }];
    while (stack.length) {
      const { account, depth, path, issue: inheritedIssue, ancestorInactive } = stack.pop();
      const id = identifier(account.id);
      if (visited.has(id)) continue;
      visited.add(id);
      const title = accountTitle(account);
      const childAccounts = children.get(id) || [];
      const missingParent = identifier(account.parent_account_id) && !unique.has(identifier(account.parent_account_id));
      const hierarchyIssue = inheritedIssue || (missingParent ? "missing-parent" : null)
        || (!ACCOUNT_KINDS[account.kind] ? "invalid-kind" : null)
        || (childAccounts.length && account.is_postable !== false ? "postable-parent" : null);
      flattened.push({ ...account, depth, label: `${"— ".repeat(depth)}${title}`, pathLabel: [...path, title].join(" / "),
        hasChildren: childAccounts.length > 0, hierarchyIssue, ancestorInactive,
        selectable: account.is_active !== false && !ancestorInactive && account.is_postable !== false && !childAccounts.length && !hierarchyIssue });
      for (let index = childAccounts.length - 1; index >= 0; index--) {
        const child = childAccounts[index];
        stack.push({ account: child, depth: depth + 1, path: [...path, title],
          issue: hierarchyIssue || (child.kind !== account.kind ? "parent-kind" : null),
          ancestorInactive: ancestorInactive || account.is_active === false });
      }
    }
  }
  roots.forEach((root) => branch(root));
  // Cyclic or disconnected legacy records must remain inspectable without an
  // infinite traversal; they are unavailable for new postings until corrected.
  [...unique.values()].sort(sortAccounts).forEach((account) => {
    if (!visited.has(identifier(account.id))) branch(account, "cycle");
  });
  const kinds = kind ? new Set(Array.isArray(kind) ? kind : [kind]) : null;
  return flattened.filter((account) => (!kinds || kinds.has(account.kind))
    && (!postableOnly || (account.is_postable !== false && !account.hasChildren && !account.hierarchyIssue)));
}

export function accountDescendants(accounts = [], parentId) {
  const result = new Set();
  const children = new Map();
  for (const account of accounts) {
    const parent = identifier(account.parent_account_id);
    if (!children.has(parent)) children.set(parent, []);
    children.get(parent).push(identifier(account.id));
  }
  const pending = [...(children.get(identifier(parentId)) || [])];
  while (pending.length) {
    const id = pending.pop();
    if (!id || result.has(id)) continue;
    result.add(id);
    pending.push(...(children.get(id) || []));
  }
  result.delete(identifier(parentId));
  return result;
}
